import { IcClose } from '@assets/icons/24x24';
import { Box, HStack, Icon, Separator, VStack } from '@chakra-ui/react';
import { CheckboxFilterItem, CheckboxListFilter } from '@components/cockpit/panels/CheckboxListFilter';
import { SearchFilterOptionEnum } from '@components/cockpit/panels/notificationSearch/notificationSearch.types';
import { useNotificationSearchContext } from '@components/cockpit/panels/notificationSearch/NotificationSearchContext';
import { getSearchCheckboxItems } from '@components/cockpit/panels/notificationSearch/searchOption.items';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasRole } from '@redux/authent/authent.selectors';
import { RootState } from '@redux/store';
import { RefObject } from 'react';
import { FormattedMessage } from 'react-intl';

import { RoleEnum } from '@/types/authent/roles.types';

interface SearchFilterOptionsProps {
  parentRef: RefObject<HTMLDivElement | null>;
  onClose: () => void;
}

export function SearchFilterOptions({ parentRef, onClose }: Readonly<SearchFilterOptionsProps>) {
  const {
    filters,
    displayedFilterOptions,
    isAllFilterOptionsDisplayed,
    isNoOptionalFilterOptionsDisplayed,
    toggleDisplayedFilterOptionValue,
    displayAllFilterOptions,
    displayNoOptionalFilterOptions,
  } = useNotificationSearchContext();
  const canAccessNFZ = useAppSelector((state: RootState) => hasRole(state, RoleEnum.ACCESS_ZONE_NFZ));

  const isChecked = (option: SearchFilterOptionEnum) => displayedFilterOptions.has(option);
  const onChange = (option: SearchFilterOptionEnum) => toggleDisplayedFilterOptionValue(option);
  const items: CheckboxFilterItem[] = getSearchCheckboxItems(filters.category, isChecked, onChange, canAccessNFZ);

  const allFilterOptionsDisplayed = isAllFilterOptionsDisplayed(filters.category);
  const noOptionalFilterOptionsDisplayed = isNoOptionalFilterOptionsDisplayed(filters.category);

  const onActionAll = () => !allFilterOptionsDisplayed && displayAllFilterOptions(filters.category);
  const onActionNone = () => !noOptionalFilterOptionsDisplayed && displayNoOptionalFilterOptions(filters.category);

  return (
    <Box position="relative" boxSize={0}>
      <VStack
        position="absolute"
        width="462px"
        height={parentRef.current?.clientHeight}
        gap={0}
        borderColor="neutral.black"
        borderWidth="0 2px"
        backgroundColor="neutral.800"
        zIndex={1}
        shadow="right"
      >
        <HStack width="100%" height="56px" padding={2} color="neutral.200">
          <FormattedMessage id="components.notificationSearch.filters.filterOptions.title" />
          <Icon marginLeft="auto" as={IcClose} boxSize={6} cursor="pointer" onClick={onClose} />
        </HStack>
        <Separator borderColor="neutral.black" />
        <Box padding={3} width="100%">
          <CheckboxListFilter
            items={items}
            actionAll={{ disabled: allFilterOptionsDisplayed, onClick: onActionAll }}
            actionNone={{ disabled: noOptionalFilterOptionsDisplayed, onClick: onActionNone }}
          />
        </Box>
      </VStack>
    </Box>
  );
}
